/**
 * Release Date/Time Web Component
 *
 * Renders the release date/time of a timed-release or raffle product in the
 * visitor's local timezone. Format: "dd.mm.yy // HH:MM AM/PM TZ".
 *
 * Sources (driven by `source` attribute):
 *  - "metafield" — reads `release-time` attribute (ISO 8601 with timezone)
 *    populated server-side from `metafields.custom.release_time`. Used by
 *    timed-release products. Label copy: "Release Date".
 *  - "rafflr"    — reads `window.raffleDates[productId].expiry` (unix seconds)
 *    populated by Rafflr's external `asset-loader.js` script. Used by raffle
 *    products (template_suffix `launch`) under settings.rafflr_version == '2.0'.
 *    Label copy: "Closes:". Listens for the `raffle-app:raffles-fetched`
 *    event on document; if data has not arrived after RAFFLR_TIMEOUT_MS, hides.
 *
 * Behavior:
 *  - On a release/expiry timestamp already in the past at connect time,
 *    hides immediately (no fallback render).
 *  - Wraps Intl formatter calls in try/catch; any failure hides the component.
 *  - Listens for `timed-release:expired` on document and hides on event.
 *  - Raffle path additionally schedules `setTimeout(hide, expiryMs - now)`
 *    to handle mid-session transitions to the closed state.
 *  - Uses connectedCallback so combined-listing innerHTML swaps re-mount cleanly.
 *
 * Usage:
 *   <release-date-time
 *     source="metafield"
 *     release-time="2026-05-02T09:30:00+10:00"
 *     product-id="123456"
 *   ></release-date-time>
 *
 *   <release-date-time
 *     source="rafflr"
 *     product-id="123456"
 *   ></release-date-time>
 */

const RAFFLR_TIMEOUT_MS = 10000;
// setTimeout stores its delay in a 32-bit signed int. Delays above this max
// overflow and fire immediately, so any timer set further out than ~24.8 days
// must be re-armed in chunks rather than scheduled in one shot.
const MAX_TIMEOUT_MS = 2147483647;

class ReleaseDateTime extends HTMLElement {
  constructor() {
    super();
    this.source = null;
    this.productId = null;
    this.timestampMs = null;
    this.expiryTimeoutId = null;
    this.rafflrTimeoutId = null;
    this.boundOnExpired = this._onExpired.bind(this);
    this.boundOnRafflrFetched = null;
  }

  connectedCallback() {
    this.source = this.getAttribute('source');
    this.productId = this.getAttribute('product-id');

    if (this.source === 'metafield') {
      const releaseAttr = this.getAttribute('release-time');
      const ms = this._parseISOToMs(releaseAttr);
      if (ms === null) {
        this._hide();
        return;
      }
      this._renderOrHide(ms, 'Release Date');
    } else if (this.source === 'rafflr') {
      this._waitForRafflrData()
        .then(({ launch, expiry }) => {
          this._renderRafflrPhase(launch * 1000, expiry * 1000);
        })
        .catch(() => this._hide());
    } else {
      this._hide();
      return;
    }

    document.addEventListener('timed-release:expired', this.boundOnExpired);
  }

  disconnectedCallback() {
    document.removeEventListener('timed-release:expired', this.boundOnExpired);
    if (this.boundOnRafflrFetched) {
      document.removeEventListener('raffle-app:raffles-fetched', this.boundOnRafflrFetched);
      this.boundOnRafflrFetched = null;
    }
    if (this.expiryTimeoutId) {
      clearTimeout(this.expiryTimeoutId);
      this.expiryTimeoutId = null;
    }
    if (this.rafflrTimeoutId) {
      clearTimeout(this.rafflrTimeoutId);
      this.rafflrTimeoutId = null;
    }
  }

  _onExpired() {
    this._hide();
  }

  _parseISOToMs(value) {
    if (!value) return null;
    // Liquid's %z format produces +1100 (no colon); JS Date wants +11:00.
    // Match existing TimedReleaseTimer normalization.
    const normalized = value.replace(/([+-])(\d{2})(\d{2})$/, '$1$2:$3');
    let date = new Date(normalized);
    if (isNaN(date.getTime())) {
      const withoutTz = normalized.replace(/[+-]\d{2}:\d{2}$/, '').replace(/Z$/, '');
      date = new Date(withoutTz + 'Z');
    }
    const ms = date.getTime();
    return isNaN(ms) ? null : ms;
  }

  _waitForRafflrData(timeout = RAFFLR_TIMEOUT_MS) {
    return new Promise((resolve, reject) => {
      const peek = () => {
        try {
          const data = window.raffleDates && window.raffleDates[this.productId];
          if (!data) return null;
          if (typeof data.launch !== 'number' || typeof data.expiry !== 'number') return null;
          return { launch: data.launch, expiry: data.expiry };
        } catch (_e) {
          return null;
        }
      };

      const initial = peek();
      if (initial !== null) {
        resolve(initial);
        return;
      }

      this.boundOnRafflrFetched = () => {
        const v = peek();
        if (v !== null) {
          if (this.rafflrTimeoutId) {
            clearTimeout(this.rafflrTimeoutId);
            this.rafflrTimeoutId = null;
          }
          document.removeEventListener('raffle-app:raffles-fetched', this.boundOnRafflrFetched);
          this.boundOnRafflrFetched = null;
          resolve(v);
        }
      };
      document.addEventListener('raffle-app:raffles-fetched', this.boundOnRafflrFetched);

      this.rafflrTimeoutId = setTimeout(() => {
        if (this.boundOnRafflrFetched) {
          document.removeEventListener('raffle-app:raffles-fetched', this.boundOnRafflrFetched);
          this.boundOnRafflrFetched = null;
        }
        this.rafflrTimeoutId = null;
        reject(new Error('rafflr-data-timeout'));
      }, timeout);
    });
  }

  /**
   * Raffle phase machine for the Rafflr v2.0 source path.
   *
   * Three phases (mirrors Rafflr's <countdown-timer> states):
   *  - upcoming: now < launch  → render `Opens: <launch date>`. Schedule
   *    a re-render at the launch moment to swap to the live phase.
   *  - live:     launch ≤ now < expiry → render `Closes: <expiry date>`.
   *    Schedule auto-hide at the expiry moment.
   *  - complete: now ≥ expiry → hide.
   */
  _renderRafflrPhase(launchMs, expiryMs) {
    if (this.expiryTimeoutId) {
      clearTimeout(this.expiryTimeoutId);
      this.expiryTimeoutId = null;
    }

    const now = Date.now();

    if (now >= expiryMs) {
      this._hide();
      return;
    }

    if (now < launchMs) {
      this._render(launchMs, 'Opens:');
      this._setExpiryTimeout(launchMs - now, () => {
        this._renderRafflrPhase(launchMs, expiryMs);
      });
      return;
    }

    this._render(expiryMs, 'Closes:');
    this._setExpiryTimeout(expiryMs - now, () => this._hide());
  }

  _render(timestampMs, labelText) {
    let formatted;
    try {
      formatted = this._format(timestampMs);
    } catch (_e) {
      this._hide();
      return;
    }
    if (!formatted) {
      this._hide();
      return;
    }
    this.timestampMs = timestampMs;
    this.style.display = '';
    this.innerHTML = `<span class="release-date-time__label">${labelText}</span> <span class="release-date-time__value">${formatted}</span>`;
  }

  _renderOrHide(timestampMs, labelText) {
    if (typeof timestampMs !== 'number' || isNaN(timestampMs)) {
      this._hide();
      return;
    }

    const now = Date.now();
    if (timestampMs <= now) {
      this._hide();
      return;
    }

    this._render(timestampMs, labelText);
    this._scheduleExpiryHide(timestampMs - now);
  }

  _format(timestampMs) {
    const date = new Date(timestampMs);

    // Date part: short weekday + zero-padded day + full month name.
    // e.g. "Mon 01 April". Force `en-GB` so the names are spelled out in
    // English regardless of browser UI locale; reassemble from parts to
    // avoid locale-specific punctuation (en-GB emits "Mon, 01 April",
    // we want "Mon 01 April").
    const dateParts = new Intl.DateTimeFormat('en-GB', {
      weekday: 'short',
      day: '2-digit',
      month: 'long'
    }).formatToParts(date);
    const getPart = (type) => {
      const part = dateParts.find((p) => p.type === type);
      return part ? part.value : '';
    };
    const datePart = `${getPart('weekday')} ${getPart('day')} ${getPart('month')}`;

    // Time part: HH:MM AM/PM (single space before AM/PM). `hour: 'numeric'`
    // may return single digit; manually zero-pad. Normalise the day-period
    // separator: some Intl locales emit U+202F (narrow no-break space)
    // instead of U+0020 — collapse to a regular space.
    const timeFmt = new Intl.DateTimeFormat('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
    const rawTime = timeFmt.format(date);
    const timePart = rawTime
      .replace(/^(\d):/, '0$1:')
      .replace(/\s+(?=[AP]M$)/i, ' ');

    const tz = this._getTimeZoneAbbreviation(date);

    const datetime = `${datePart} - ${timePart}`;
    return tz ? `${datetime} ${tz}` : datetime;
  }

  /**
   * Resolve a named timezone abbreviation (e.g. "AEDT", "AEST", "EST", "JST")
   * for the given date in the user's local timezone.
   *
   * `Intl.DateTimeFormat` with `timeZoneName: 'short'` is inconsistent across
   * browsers and locales — Chrome with `en-US` often returns `GMT+11` for
   * non-US zones, while `en-AU` / `en-GB` return the named abbreviation.
   * Try locales in order, accept the first non-GMT/UTC value, and fall back
   * to building an acronym from the long name (e.g. "Australian Eastern
   * Daylight Time" → "AEDT").
   */
  _getTimeZoneAbbreviation(date) {
    const locales = ['en-AU', 'en-GB', 'en-US'];
    for (const locale of locales) {
      try {
        const parts = new Intl.DateTimeFormat(locale, {
          timeZoneName: 'short',
          hour: 'numeric'
        }).formatToParts(date);
        const tz = parts.find((p) => p.type === 'timeZoneName');
        if (tz && tz.value && !/^(GMT|UTC)/i.test(tz.value)) {
          return tz.value;
        }
      } catch (_e) {
        // ignore and try next locale
      }
    }

    try {
      const parts = new Intl.DateTimeFormat('en-US', {
        timeZoneName: 'long',
        hour: 'numeric'
      }).formatToParts(date);
      const tz = parts.find((p) => p.type === 'timeZoneName');
      if (tz && tz.value) {
        const acronym = tz.value
          .split(/\s+/)
          .map((word) => word.charAt(0))
          .filter((c) => /[A-Z]/.test(c))
          .join('');
        if (acronym.length >= 2) return acronym;
      }
    } catch (_e) {
      // ignore
    }

    return '';
  }

  /**
   * setTimeout wrapper that survives delays beyond the 32-bit max (~24.8 days).
   * Re-arms itself in MAX_TIMEOUT_MS chunks until the remaining delay fits in
   * a single timer, then fires `callback`. The active timer id is stored on
   * `this.expiryTimeoutId` so disconnectedCallback can still clear it.
   */
  _setExpiryTimeout(deltaMs, callback) {
    if (this.expiryTimeoutId) {
      clearTimeout(this.expiryTimeoutId);
      this.expiryTimeoutId = null;
    }
    if (deltaMs > MAX_TIMEOUT_MS) {
      this.expiryTimeoutId = setTimeout(() => {
        this._setExpiryTimeout(deltaMs - MAX_TIMEOUT_MS, callback);
      }, MAX_TIMEOUT_MS);
      return;
    }
    this.expiryTimeoutId = setTimeout(callback, deltaMs);
  }

  _scheduleExpiryHide(deltaMs) {
    if (typeof deltaMs !== 'number' || deltaMs <= 0) {
      if (this.expiryTimeoutId) {
        clearTimeout(this.expiryTimeoutId);
        this.expiryTimeoutId = null;
      }
      this._hide();
      return;
    }
    this._setExpiryTimeout(deltaMs, () => this._hide());
  }

  _hide() {
    this.style.display = 'none';
  }
}

if (!customElements.get('release-date-time')) {
  customElements.define('release-date-time', ReleaseDateTime);
}
